import { computed, effect, Injectable, signal } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import {
  DropBallOptions,
  PlinkoMultiplier,
  PlinkoResult,
  PlinkoRisk,
  PlinkoRow,
} from './interfaces/plinko.interface';
import { SocketService } from '@core/services/socket.service';
import { SessionService } from '@core/services/session.service';
import { sleep } from '@core/utils/sleep.util';

@Injectable({
  providedIn: 'root',
})
export class PlinkoService {
  balls = signal<DropBallOptions[]>([]);

  currentRows = signal<number>(8);
  currentRisk = signal<PlinkoRisk>('high');
  currentMultipliers = computed<PlinkoMultiplier[]>(() => {
    const row = this.plinkoTable().find(
      (row) =>
        row.rows === this.currentRows() && row.risk === this.currentRisk()
    );
    if (!row) {
      return [];
    }

    return row.payouts;
  });
  plinkoTable = signal<PlinkoRow[]>([]);
  running = signal(false);
  animSpeed = signal<number>(4);
  MIN_BET = 0.06;
  MAX_BET = 50;

  constructor(
    private socketService: SocketService,
    private toastrService: ToastrService,
    private session: SessionService
  ) {
    this.socketService.socket.on('plinko_multipliers', (data: PlinkoRow[]) => {
      this.plinkoTable.set(data);
    });

    this.preloadImages();

    const resetBallsEffect = effect(() => {
      const hasChanged = this.currentMultipliers();
      if (hasChanged) {
        this.balls.set([]);
      }
    });
  }

  setRows(rows: number) {
    this.currentRows.set(Number(rows));
  }

  setRisk(risk: PlinkoRisk) {
    this.currentRisk.set(risk);
  }

  dropBall(options: DropBallOptions) {
    this.balls.update((prev) => [...prev, options]);
  }

  async placeBet(balls: number, value: number) {
    if (this.running() && balls > 2) {
      return;
    }

    if (balls > 50 || balls < 1) {
      this.toastrService.error(
        'You can only drop between 1 and 50 balls at a time.'
      );
      return;
    }

    if (value < this.MIN_BET) {
      this.toastrService.error(`Minimum bet is $${this.MIN_BET}.`);
      return;
    }

    if (this.session.currentBalance() < balls * value) {
      this.toastrService.error(
        'You do not have enough balance to place this bet.'
      );
      return;
    }

    try {
      const balType = this.session.balanceType();

      const result: PlinkoResult[] = await this.socketService.emit(
        'play_plinko',
        {
          rows: this.currentRows(),
          risk: this.currentRisk(),
          amount: balls,
          betAmount: +value,
          balance: balType,
        }
      );

      this.session.balance.update((prev) => {
        const newVal = +(prev[balType] - balls * value).toFixed(2);
        return {
          ...prev,
          [balType]: newVal,
        };
      });
      // console.log('play_plinko');
      this.running.set(true);
      if (Array.isArray(result)) {
        for (const ball of result) {
          // console.log('🚀 ~ BACKEND FROM SERVICE:', ball.pocket);

          this.dropBall({
            color: 1,
            point: ball.point,
            value: value,
            result: ball,
          });
          // console.log('🚀 ~ BACKEND FROM SERVICE2:', ball.pocket);

          await sleep(100);
        }
      }

      this.running.set(false);
    } catch (e) {
      console.log('🚀 ~ PlinkoService ~ placeBet ~ e:', e);
    }
  }

  private preloadImages() {
    const images = [
      'assets/old-images/plinko/peg.png',
      'assets/old-images/plinko/ball.png',
    ];

    for (const image of images) {
      const img = new Image();
      img.src = image;
    }
  }
}
